/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Div,
  ErrorToast,
  Icon,
  Img,
  PlusIcon,
  TrashIconRed,
  lightTheme,
  uploadImportPreviewImage,
} from '@vgrubs/components';
import { useState } from 'react';

/**
 * Multi-image picker that uploads each selected file via the existing
 * `uploadImportPreviewImage` endpoint and returns the resulting URL strings.
 * Reused by the AI controls panel, the refine-item modal and the
 * generate-image modal (all of which send `sampleImageUrls` / `example` URLs).
 */
export const SampleImagesInput = ({
  locationId,
  brandId,
  urls,
  onChange,
  max = 4,
  disabled,
}: {
  locationId: string | number;
  brandId: string | number;
  urls: string[];
  onChange: (urls: string[]) => void;
  max?: number;
  disabled?: boolean;
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e: any) => {
    const files: File[] = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const room = max - urls.length;
    if (room <= 0) {
      ErrorToast(`You can add up to ${max} images.`);
      return;
    }

    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of files.slice(0, room)) {
        const formData = new FormData();
        formData.append('image', file);
        const { data } = await uploadImportPreviewImage(
          locationId,
          brandId,
          formData
        );
        if (data.data) uploaded.push(data.data);
      }
      onChange([...urls, ...uploaded]);
    } catch (error) {
      ErrorToast('Failed to upload image.');
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (index: number) =>
    onChange(urls.filter((_, i) => i !== index));

  const canAdd = !disabled && !uploading && urls.length < max;

  return (
    <Div $flex $gap="12px" style={{ flexWrap: 'wrap' }}>
      {urls.map((url, i) => (
        <Div
          key={url + i}
          style={{ position: 'relative', width: '72px', height: '72px' }}
        >
          <Img
            src={url}
            style={{
              width: '72px',
              height: '72px',
              objectFit: 'cover',
              borderRadius: '12px',
            }}
          />
          {!disabled && (
            <Icon
              $pointer
              onClick={() => removeAt(i)}
              style={{ position: 'absolute', top: '-8px', right: '-8px' }}
            >
              <TrashIconRed />
            </Icon>
          )}
        </Div>
      ))}

      {canAdd && (
        <label
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '12px',
            border: `1px dashed ${lightTheme.lightGray}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Icon color={lightTheme.primary}>
            <PlusIcon />
          </Icon>
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleFiles}
          />
        </label>
      )}

      {uploading && (
        <Div
          $flex
          $center
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '12px',
            border: `1px dashed ${lightTheme.lightGray}`,
            opacity: 0.6,
            fontSize: '20px',
          }}
        >
          …
        </Div>
      )}
    </Div>
  );
};
